import { useTranslation } from 'react-i18next';

import {
  Flex,
  Chip,
  Text,
  Skeleton,
  spaces,
  sizes,
  useThemeColors,
  ColorsV2,
} from '@electricjs/arc';

import { ExternalLink } from '@common';

import { Product } from '@/types/products';
import { AppleDepServerTokenStateEnum } from '@/types/mdmManagement';

import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useGetMDMInformationQuery } from '@/redux/slices/mdmManagementApiSlice';
import { getLocalizedSupportUrl } from '@/helpers/getLocalizedSupportUrl';

import {
  isAppleDevice,
  isWindowsDeviceZtpEligible,
  isWindowsDeviceNotZtpEligible,
  isWindowsAutopilotEligible,
} from '../../utils/helpers';

import { useStorefront } from '../../StorefrontProvider';

import { ZtpEnabledChip } from './ZtpEnabledChip';
import { ZtpNotEnabledChip } from './ZtpNotEnabledChip';

type ItemZtpStatusProps = {
  item: Product;
};

export const ItemZtpStatus = ({ item }: ItemZtpStatusProps) => {
  const { t } = useTranslation('storefront');

  const [blueChipTextColor, blueChipBackgroundColor] = useThemeColors([
    ColorsV2.INFO_DARK,
    ColorsV2.INFO_LIGHTEST,
  ]);

  const {
    storefrontCheckoutForm,
    canConfigureWinZtp,
    canConfigureWinAutopilot,
  } = useStorefront();

  const organizationId = useGetOrganizationId();

  const {
    data: mdmInformation,
    isFetching: isFetchingMDMInformation,
    error: mdmInformationError,
  } = useGetMDMInformationQuery({ organizationId });

  const isAppleMDMReady =
    !isFetchingMDMInformation &&
    !mdmInformationError &&
    mdmInformation?.appleMdmDepInfo?.depServerTokenState ===
      AppleDepServerTokenStateEnum.VALID;

  const windowsZtpEnabled = storefrontCheckoutForm?.watch('windowsZtpEnabled');
  const wAutopilotEnabled = storefrontCheckoutForm?.watch('wAutopilotEnabled');

  if (isFetchingMDMInformation) {
    return <Skeleton width="15rem" height={sizes.size_24_rem} />;
  }

  if (isAppleDevice(item) && isAppleMDMReady) {
    return <ZtpEnabledChip enablementType="abm" />;
  }

  if (isAppleDevice(item) && !isAppleMDMReady) {
    return (
      <Flex
        columnGap={spaces.space_xs_rem}
        rowGap={spaces.space_xs_rem}
        flexWrap="wrap"
        alignItems="center">
        <ZtpNotEnabledChip />
        <Text variant="body-2">{t('ztp.itemStatus.manualSetupRequired')}</Text>
        <ExternalLink
          id="ztp-learn-more-link"
          href={getLocalizedSupportUrl(
            'https://support.electric.ai/electricithub/electric-it-hub-utilizing-zero-touch-provisioning-#ElectricITHub%7CUtilizingZeroTouchProvisioningofDevices-FAQs'
          )}>
          {t('ztp.itemStatus.learnMore')}
        </ExternalLink>
      </Flex>
    );
  }

  // WAutopilot: toggle on and item is eligible
  if (wAutopilotEnabled && isWindowsAutopilotEligible(item)) {
    return <ZtpEnabledChip enablementType="wAutopilotEnrollment" />;
  }

  // Both services available, neither toggle on, item qualifies for both
  if (
    canConfigureWinAutopilot &&
    canConfigureWinZtp &&
    isWindowsAutopilotEligible(item) &&
    isWindowsDeviceZtpEligible(item) &&
    !windowsZtpEnabled &&
    !wAutopilotEnabled
  ) {
    return (
      <Chip
        id="win-autopilot-or-ztp-eligible"
        icon="circle-check"
        variant="small"
        textColor={blueChipTextColor}
        backgroundColor={blueChipBackgroundColor}>
        {t('wautopilot.chips.eligibleForAutopilotOrZtp')}
      </Chip>
    );
  }

  // Only WAutopilot available (item is desktop — Autopilot-eligible but not ZTP-eligible), toggle off
  if (
    canConfigureWinAutopilot &&
    isWindowsAutopilotEligible(item) &&
    !isWindowsDeviceZtpEligible(item) &&
    !wAutopilotEnabled
  ) {
    return (
      <Chip
        id="win-autopilot-eligible"
        icon="circle-check"
        variant="small"
        textColor={blueChipTextColor}
        backgroundColor={blueChipBackgroundColor}>
        {t('wautopilot.chips.eligible')}
      </Chip>
    );
  }

  // only when the user can configure Win ZTP
  if (canConfigureWinZtp) {
    if (isWindowsDeviceNotZtpEligible(item)) {
      return (
        <Chip id="win-ztp-not-eligible" icon="shield-slash" variant="small">
          {t('ztp.chips.notEligible')}
        </Chip>
      );
    }

    if (isWindowsDeviceZtpEligible(item) && windowsZtpEnabled) {
      return <ZtpEnabledChip enablementType="preConfiguration" />;
    }

    if (isWindowsDeviceZtpEligible(item) && !windowsZtpEnabled) {
      return (
        <Flex
          columnGap={spaces.space_xs}
          rowGap={spaces.space_xs}
          flexWrap="wrap"
          alignItems="center">
          <ZtpNotEnabledChip />
          <Chip
            id="win-ztp-eligible"
            icon="circle-check"
            variant="small"
            textColor={blueChipTextColor}
            backgroundColor={blueChipBackgroundColor}>
            {t('ztp.chips.eligible')}
          </Chip>
        </Flex>
      );
    }
  }

  // Any other combination won't be shown
  return null;
};
